/**
 * The client-side workflow binding for Solid modules — the slim replacement for the
 * protocol layer baked into mmodWorkflow (ported from the bosy redesign). It does exactly
 * three things and writes them into one slice of the owning module's reactive store:
 *   1. fetch the model (`mmModelGet` → `mmModel`) and build the msysWorkflow engine
 *      client-side for formForAction / available-actions / file-field encoding,
 *   2. watch the object list (`mmWatch` → pushed `mmObjects`) and reconcile it,
 *   3. dispatch actions (`mmAction`) and fold the echoed `mmObjects` back in.
 *
 * # The sliding row window
 * The held rows are a WINDOW into the server-sorted result set, sized for a 100k-record
 * table: `window_start` is the server offset of the first held row, `max_rows` caps the
 * client memory. Scrolling down appends pages and evicts from the top; scrolling back up
 * prepends the previous page and evicts from the bottom. Live watch pushes update rows
 * present in the window in place; a NEW record is inserted only while the window sits at
 * the top of the result set (where the default newest-first sort would show it anyway).
 *
 * Everything else mmodWorkflow bakes in (popup forms, export, the SVG diagram, log tables,
 * keypress nav, URL sync) is GUI policy a module layers on top — not the client's job.
 * Model translations register into LANG automatically via the msysWorkflow constructor.
 */

import msysWorkflow from 'msysWorkflow'

const EMPTY_SLICE = { model: null, list: [], version: 0, total: 0, offset: 0, window_start: 0, loading: false, paging: false, error: null }

export default class WorkflowClient {
  /**
   * @param {object} module - owning mmodSolidModule (messaging + reactive store + engine context)
   * @param {object} opts - client options
   * @param {string} opts.server - bus address of the server workflow (e.g. ':msvrTzRecords')
   * @param {string} [opts.key] - store slice key (default 'wf')
   * @param {boolean} [opts.watch] - live watch (default true) vs one-shot mmGet
   * @param {object} [opts.query] - server-side filter for the watched list
   * @param {Array<string>} [opts.fields] - list-row fields (keep heavy blobs OUT; targeted
   *   fetchFull always pulls complete records)
   * @param {array} [opts.sort] - server-side sort criteria
   * @param {integer} [opts.limit] - page size (default 100)
   * @param {integer} [opts.max_rows] - sliding window cap (default 1500)
   * @param {boolean} [opts.actions] - include per-row available actions (default false — the
   *   detail view refetches with them; saves an ACL evaluation per row and page)
   * @param {function} [opts.on_model] - called with the model after it arrives
   */
  constructor(module, opts) {
    this.module = module
    this.opts = _.defaults({}, opts, { key: 'wf', watch: true, actions: false, limit: 100, max_rows: 1500 })
    this.rows = [] // the held window, ordered as the server serves it
    this.ids = new Map() // id → row (membership + merge source)
    this.window_start = 0 // server offset of rows[0]
    module.setStore(this.opts.key, { ...EMPTY_SLICE })
  }

  get key() { return this.opts.key }
  get server() { return this.opts.server }

  /** A held record by id (null when outside the current window). */
  getRecord(id) {
    return this.ids.get(_.toString(id)) || null
  }

  /** Fetch the model, then register the watch (or one-shot mmGet). */
  async start() {
    const m = this.module
    m.setStore(this.key, 'loading', true)
    try{
      const reply = await m.request(this.server, 'mmModelGet', {}, ['mmModel'])
      if(!this.applyModel(reply.params)) {
        // the server answers an EMPTY model when the actor fails the workflow's allow -
        // say so instead of silently running on wf.generic.* fallback keys
        m.setStore(this.key, 'error', m.t('wf.generic.NoAccess'))
        m.setStore(this.key, 'loading', false)
        return
      }
      m.setStore(this.key, 'error', null)
    } catch(e) {
      m.setStore(this.key, 'error', _.toString(e?.params?.error || e?.message || e))
    }
    m.setStore(this.key, 'loading', false)

    const params = this.listParams()
    if(this.opts.watch) {
      // register the watch; the server pushes mmObjects to our listener_id
      m.publish(this.server, 'mmWatch', { version: 0, ...params })
    } else {
      try{
        const reply = await m.request(this.server, 'mmGet', params, ['mmObjects'])
        this.reconcile(reply.params)
      } catch(e) {
        m.setStore(this.key, 'error', _.toString(e?.params?.error || e?.message || e))
      }
    }
  }

  /** Unregister the watch (mmWatch without a version = unwatch). */
  stop() {
    if(this.opts.watch) this.module.publish(this.server, 'mmWatch', {})
  }

  /** Replace the server-side query and re-sync from the first page. */
  setQuery(query) {
    this.opts.query = query
    this.reset()
    this.refresh()
  }

  /** Replace the server-side sort and re-sync from the first page. */
  setSort(sort) {
    this.opts.sort = sort
    this.reset()
    this.refresh()
  }

  reset() {
    this.rows = []
    this.ids.clear()
    this.window_start = 0
    this.module.setStore(this.key, 'list', [])
    this.module.setStore(this.key, 'window_start', 0)
  }

  /** Re-register the watch / re-run the one-shot get with current params. */
  refresh() {
    const m = this.module
    if(this.opts.watch) {
      m.publish(this.server, 'mmWatch', { version: 0, ...this.listParams() })
    } else {
      m.request(this.server, 'mmGet', this.listParams(), ['mmObjects'])
        .then(reply=>this.reconcile(reply.params))
        .catch(e=>m.setStore(this.key, 'error', _.toString(e?.params?.error || e?.message || e)))
    }
  }

  /** Route a pushed mmObjects message here from the module's handler. */
  mmObjects(sender, params) {
    this.reconcile(params)
  }

  /** Scroll down: fetch the page after the window and merge it in (evicts from the top). */
  async loadMore() {
    const slice = this.slice()
    const loaded_end = this.window_start + this.rows.length
    if(this._paging || (slice.total>0 && loaded_end>=slice.total)) return
    this._paging = true
    this.module.setStore(this.key, 'paging', true)
    try{
      const reply = await this.module.request(this.server, 'mmGet', {
        version: 1, // prevents a `full` snapshot response
        ...this.listParams(),
        offset: loaded_end,
      }, ['mmObjects'])
      this.reconcile(reply.params, { page: true })
    }catch(e) {
      this.module.setStore(this.key, 'error', _.toString(e?.params?.error || e?.message || e))
    }finally{
      this._paging = false
      this.module.setStore(this.key, 'paging', false)
    }
  }

  /**
   * Scroll up past the window start: fetch the page before the window and prepend it
   * (evicts from the bottom). Returns the number of prepended rows so the caller can
   * keep the scroll position steady.
   */
  async loadPrevious() {
    if(this._paging || this.window_start<=0) return 0
    this._paging = true
    this.module.setStore(this.key, 'paging', true)
    const before = this.rows.length
    const offset = Math.max(0, this.window_start - this.opts.limit)
    try{
      const reply = await this.module.request(this.server, 'mmGet', {
        version: 1,
        ...this.listParams(),
        offset,
        limit: this.window_start - offset, // never overlap the window
      }, ['mmObjects'])
      this.reconcile(reply.params, { prepend: true, prepend_offset: offset })
      return Math.max(0, this.rows.length - before) // net growth ≈ prepended (minus tail eviction)
    }catch(e) {
      this.module.setStore(this.key, 'error', _.toString(e?.params?.error || e?.message || e))
      return 0
    }finally{
      this._paging = false
      this.module.setStore(this.key, 'paging', false)
    }
  }

  /**
   * Fetch ONE record with all fields + history logs on demand (list rows are slim by
   * design). The row updates in place when it is inside the window; the full record is
   * returned either way.
   */
  async fetchFull(id, extra = {}) {
    const reply = await this.module.request(this.server, 'mmGet', {
      version: 1, // prevents a `full` snapshot response wiping the list
      query: { id },
      actions: true,
      ...extra,
    }, ['mmObjects'])
    this.reconcile(reply.params)
    return this.getRecord(id) || this.prepared(reply.params?.list?.[0]) || null
  }

  /**
   * Perform a workflow action; the echoed mmObjects reconciles back into the list.
   * @returns {object} the reply message ({ action, params })
   */
  async action(id, action, params = {}) {
    const reply = await this.module.request(this.server, 'mmAction', { id, action, params }, ['mmSuccess', 'mmObjects'])
    if(reply.action==='mmObjects') this.reconcile({ ...reply.params, requested_id: id })
    return reply
  }

  /** Actions available on a record: the model's state actions filtered by the server's ACL echo. */
  actionsFor(record) {
    const states = this.engine?.states || this.slice().model?.states || {}
    const all = Object.keys(_.get(states, [record?.state ?? '', 'actions'], {}))
    const allowed = Array.isArray(record?._actions) ? record._actions : null
    return allowed ? all.filter(a=>allowed.includes(a)) : all
  }

  /** Form key the model prescribes for the action on this record. */
  formFor(record, action) {
    return _.toString(this.engine?.formForAction(record, action))
  }

  /** Encode file fields before submitting a record (delegates to the engine). */
  encodeFiles(old_record, new_record) {
    this.engine?.encodeFileFields(old_record, new_record)
  }

  /** The reactive slice this client maintains. */
  slice() {
    return this.module.store[this.key]
  }

  // ---- internals -------------------------------------------------------------------------------

  listParams() {
    const { query, fields, sort, limit, actions } = this.opts
    const params = {}
    if(query) params.query = query
    if(fields!==undefined) params.fields = fields
    if(sort) params.sort = sort
    if(limit!==undefined) params.limit = limit
    if(actions) params.actions = true
    return params
  }

  /** @returns {boolean} false = no usable model (empty reply: the actor has no access) */
  applyModel(model) {
    if(!model || !model.states) return false // empty reply (access denied) or md5-stub (we never send md5)
    try{
      // context_object = the module; the constructor also registers `wf.<id>.` translations into LANG
      this.engine = new msysWorkflow(this.module, model)
    } catch(e) {
      delete this.engine // model unusable as a def — keep the raw model for lookups
    }
    this.module.setStore(this.key, 'model', model)
    if(this.opts.on_model) this.opts.on_model(model)
    return true
  }

  /** Merge-prepare one incoming item against the held copy (limited fields, files, logs, actions). */
  prepared(item, params = {}) {
    if(!item) return null
    const id = _.toString(item.id)
    const old = this.ids.get(id) || {}
    item._limited = !!params.limited
    if(this.engine?.decodeFileFields(old, item)) item._limited = true
    // a limited result carries only some fields — keep the rest from the previous record
    if(item._limited) _.defaults(item, _.omit(old, ['_limited']))
    // log records are served only on a targeted `logs: true` get — merge by log_id so a
    // later log-less update (watch delta, action echo) doesn't wipe an already-loaded history
    if(Array.isArray(item._logs) && Array.isArray(old._logs)) {
      const merged = {}
      for(const log of old._logs) merged[log.log_id] = log
      for(const log of item._logs) merged[log.log_id] = log
      item._logs = Object.values(merged)
    }else if(!Array.isArray(item._logs) && Array.isArray(old._logs)) {
      item._logs = old._logs
    }
    // same for the per-record ACL echo: an action reply carries no _actions
    if(!Array.isArray(item._actions) && Array.isArray(old._actions)) {
      item._actions = old._actions
    }
    return item
  }

  /**
   * Merge a pushed/echoed mmObjects payload into the window.
   * opts.page / opts.prepend mark explicit page fetches; everything else is a delta.
   */
  reconcile(params, opts = {}) {
    if(!params || !Array.isArray(params.list)) return
    if(params.full) {
      this.rows = []
      this.ids.clear()
      this.window_start = 0
    }
    // the matching-row total is meaningful only on a full snapshot or an explicit page —
    // incremental watch pushes carry the count of CHANGED rows instead
    if((params.full || opts.page || opts.prepend) && params.total!=null) {
      this.module.setStore(this.key, 'total', Number(params.total) || 0)
    }

    const fresh = [] // new rows of an explicit page/snapshot, in server order
    for(const raw of params.list) {
      const item = this.prepared(raw, params)
      const id = _.toString(item.id)
      if(item.state==='WIPED') {
        if(this.ids.has(id)) {
          this.rows.splice(this.rows.findIndex(row=>_.toString(row.id)===id), 1)
          this.ids.delete(id)
        }
        continue
      }
      if(this.ids.has(id)) {
        // update in place — the window keeps its server order
        this.rows[this.rows.findIndex(row=>_.toString(row.id)===id)] = item
        this.ids.set(id, item)
      }else if(params.full || opts.page || opts.prepend) {
        fresh.push(item)
        this.ids.set(id, item)
      }else if(this.window_start===0) {
        // delta for an unheld record: insert only while the window sits at the top of the
        // result set (the client-side sort places it); deeper windows ignore it
        this.rows.push(item)
        this.ids.set(id, item)
      }
    }

    if(opts.prepend) {
      this.rows = [...fresh, ...this.rows]
      this.window_start = Number(opts.prepend_offset) || 0
      // evict from the bottom beyond the cap
      if(this.rows.length>this.opts.max_rows) {
        for(const row of this.rows.splice(this.opts.max_rows)) this.ids.delete(_.toString(row.id))
      }
    }else{
      this.rows = [...this.rows, ...fresh]
      // evict from the top beyond the cap (the rows scrolled past)
      if((params.full || opts.page) && this.rows.length>this.opts.max_rows) {
        const evicted = this.rows.splice(0, this.rows.length - this.opts.max_rows)
        for(const row of evicted) this.ids.delete(_.toString(row.id))
        this.window_start += evicted.length
      }
    }

    this.module.setStore(this.key, 'list', [...this.rows])
    this.module.setStore(this.key, 'window_start', this.window_start)
    this.module.setStore(this.key, 'offset', this.window_start + this.rows.length)
    if(params.version!=null) this.module.setStore(this.key, 'version', params.version)
  }
} // WorkflowClient
